import { type AdminFeatureOptions, type ListBundlesFn } from '@/features/admin'
import { AdminApi } from '@/features/admin/api/admin-api'
import { AdminIamApi } from '@/features/admin/api/admin-iam-api'
import { BundleStore } from '@/features/admin/bundle/BundleStore'
import { AccountMergeRequestStore } from '@/features/admin/account-linking/AccountMergeRequestStore'
import { env } from '@/infra/env'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { localStorage } from '@/infra/storage'
import { memoize } from '@/utils/memoize-util'
import { AdminViewModel } from './AdminViewModel'
import { BookkeepingManagerStore } from '@/features/admin/bookkeeping/BookkeepingManagerStore'
import { ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'

/**
 * Function to acquire the admin view model.
 */
export type AcquireAdminViewModelFn = (
  signal?: AbortSignal,
) => Promise<AdminViewModel>

export function InternalAdminFeature(options: AdminFeatureOptions) {
  const api = new AdminApi(
    { baseURL: env.WORK_DOTNET_API, token$: options.token$ },
    options.workRealtime$,
  )
  const iamApi = new AdminIamApi({
    baseURL: env.IAM_API,
    token$: options.token$,
  })

  const bundleStore = new BundleStore(
    api,
    options.primeUser,
    options.primeWorkspace,
  )

  const workspacePostingStore = new WorkspacePostingStore(
    iamApi,
    options.primeWorkspace,
    options.primeUser,
    options.primeProvider,
  )

  const bookkeepingManagerStore = new BookkeepingManagerStore(
    api,
    options.primeMemberByPublicId,
  )

  const accountMergeRequestStore = new AccountMergeRequestStore(iamApi)

  const listBundles: ListBundlesFn = (query, signal) =>
    bundleStore.listBundles(query, signal)

  const acquireAdminViewModel: AcquireAdminViewModelFn = memoize(
    async (signal?: AbortSignal) => {
      signal?.throwIfAborted()

      const primeJob = await options.primeJob
      const scopeChangeRequestStore = new ScopeChangeRequestStore(
        api,
        options.primeWorkspace,
        options.primeUser,
        primeJob,
      )

      return new AdminViewModel(
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        options.searchInventoryItems,
        options.fetchInventoryItemsByIds,
        bundleStore,
        scopeChangeRequestStore,
        options.listWorkspaceMemberships,
        workspacePostingStore,
        options.canViewPayoutsAdmin,
        bookkeepingManagerStore,
        accountMergeRequestStore,
        (ns) => new FilterStorage(localStorage, ns),
        options.fetchJobs,
        options.currentMember$,
        options.getCurrentMember,
        options.getCurrentUserWorkspaceMemberships,
        env.HUBSPOT_PORTAL_ID,
      )
    },
    { promise: ([signal]) => signal },
  )

  return {
    // Internal
    acquireAdminViewModel,
    listBundles,
  }
}
